import React, { useState } from 'react';
import { Cpu, Database, TrendingUp, ArrowRight, AlertTriangle, ChevronDown, ChevronUp, Printer } from 'lucide-react';
import { ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer } from 'recharts';
import { useDashboard } from '../../DashboardContext';
import { S, STATE_TAX_RATES, SE_TAX } from '../../lib/constants';
import InfoTooltip from '../shared/InfoTooltip';

export default function TaxEconomicsSection() {
  const {
    miningEconomics,
    operatorEconomics,
    taxStructureComparison,
    dcaVsMiningComparison,
    showAfterTaxEconomics, setShowAfterTaxEconomics,
    taxRate, setTaxRate,
    stateTaxRate,
    stateCode, setStateCode,
    w2SSWages, setW2SSWages,
    otherSEIncome, setOtherSEIncome,
    entityType, setEntityType,
    ownerResidency, setOwnerResidency,
    llcManagementLocation, setLLCManagementLocation,
    foreignTaxRegime, setForeignTaxRegime,
    incomeType, setIncomeType,
    feieApplies, feieCoversIncome, feieRemaining, effectiveIncomeTaxRate, FEIE_EXCLUSION,
    stateConformsBonus,
    useBonusDepreciation,
    scorpSalaryPercent, setScorpSalaryPercent,
    filingStatus, setFilingStatus,
    qbiEnabled,
    timeHorizon,
    fmt,
  } = useDashboard();

  const [showAdvanced, setShowAdvanced] = useState(false);

  if (!miningEconomics) {
    return <div className="text-sm italic p-4" style={{ color: '#737373' }}>Set up this plan's configuration to see mining economics and tax analysis.</div>;
  }

  const inputStyle = {
    width: '100%', background: '#F5F5F5', border: '1px solid #E5E5E5',
    borderRadius: '4px', padding: '8px 12px', fontSize: '14px', color: '#1A1A1A',
    fontFamily: "'IBM Plex Mono', monospace",
  };
  const selectStyle = { ...inputStyle, cursor: 'pointer' };
  const fieldLabelStyle = { fontSize: '11px', color: '#525252', display: 'block', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.04em' };

  const isScorp = entityType === 'scorp';
  const isCcorp = entityType === 'ccorp';

  const stateLabel = (STATE_TAX_RATES.find(s => s.code === stateCode) || STATE_TAX_RATES[0]).label;
  const entityLabels = { llc: 'LLC (Pass-through)', mmllc_foreign: 'MMLLC (Foreign)', scorp: 'S-Corp', ccorp: 'C-Corp' };

  return (
    <div className="space-y-6">
      {/* Print-only report header */}
      <div className="print-only screen-hidden print-deliverable">
        <div style={{ borderBottom: '2px solid #1A1A1A', paddingBottom: '12px', marginBottom: '16px' }}>
          <div style={{ fontSize: '22px', fontWeight: 700, color: '#1A1A1A', fontFamily: "'Outfit', sans-serif" }}>
            Miner<span style={{ color: '#92700C' }}>Ops</span> · Tax & Structure Analysis
          </div>
          <div style={{ fontSize: '12px', color: '#737373', marginTop: '4px', fontFamily: "'IBM Plex Mono', monospace" }}>
            Generated {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            {' · '}{entityLabels[entityType] || entityType} · {filingStatus === 'mfj' ? 'MFJ' : 'Single/HoH'}
            {' · '}{feieCoversIncome ? `${effectiveIncomeTaxRate.toFixed(1).replace(/\.0$/, '')}% Fed (FEIE)` : `${taxRate}% Fed`} + {stateTaxRate}% {stateLabel} = {(effectiveIncomeTaxRate + stateTaxRate).toFixed(1).replace(/\.0$/, '')}% combined
            {' · '}{timeHorizon}-year horizon
          </div>
        </div>
      </div>

      {/* Screen: Print button */}
      {showAfterTaxEconomics && taxStructureComparison && (
        <div className="flex justify-end no-print">
          <button
            onClick={() => window.print()}
            className="flex items-center gap-2 px-4 py-2 rounded font-medium text-sm transition-all"
            style={{ background: '#1A1A1A', color: '#FFFFFF', border: 'none', cursor: 'pointer' }}
          >
            <Printer className="w-4 h-4" />
            Print Report
          </button>
        </div>
      )}

      {/* Tax & Structure */}
      <div className="print-deliverable">
        <div className="overflow-hidden" style={{ ...S.card, borderRadius: '8px' }}>
          <div className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold flex items-center gap-2" style={{ color: '#525252', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                <Database className="w-4 h-4" style={{ color: S.C.purple }} /> Tax & Structure Analysis
              </h3>
              <label className="flex items-center gap-2 text-xs cursor-pointer no-print" style={{ color: '#525252' }}>
                <input type="checkbox" checked={showAfterTaxEconomics} onChange={(e) => setShowAfterTaxEconomics(e.target.checked)} className="rounded accent-purple-500" />
                Enable tax analysis
              </label>
            </div>

            {!showAfterTaxEconomics ? (
              <div className="text-sm italic" style={{ color: '#737373' }}>Enable tax analysis to configure your tax structure and see recommendations.</div>
            ) : (
              <div className="space-y-4">

                {/* ═══════ SECTION 1: Client Profile ═══════ */}
                <div className="p-4" style={{ ...S.innerCard, borderRadius: '8px' }}>
                  <h4 className="text-sm font-semibold mb-1" style={{ color: '#1A1A1A' }}>Client Profile</h4>
                  <p className="text-xs mb-4" style={{ color: '#737373' }}>Basic info you can gather on a call. It drives every tax number below</p>

                  {/* Row 1: Business Structure */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <InfoTooltip text="How is the client's existing business structured? This determines SE tax, payroll, and deduction eligibility.">
                        <label style={fieldLabelStyle}>Entity Type</label>
                      </InfoTooltip>
                      <select value={entityType} onChange={e => setEntityType(e.target.value)} style={selectStyle}>
                        <option value="llc">LLC (Pass-through)</option>
                        <option value="mmllc_foreign">MMLLC (Foreign)</option>
                        <option value="scorp">S-Corp</option>
                        <option value="ccorp">C-Corp</option>
                      </select>
                    </div>
                    <div>
                      <InfoTooltip text="Single, Head of Household, or Married Filing Jointly. Affects Excess Business Loss (EBL) limits.">
                        <label style={fieldLabelStyle}>Filing Status</label>
                      </InfoTooltip>
                      <select value={filingStatus} onChange={e => setFilingStatus(e.target.value)} style={selectStyle}>
                        <option value="single">Single / HoH</option>
                        <option value="mfj">Married Filing Jointly</option>
                      </select>
                    </div>
                    <div>
                      <InfoTooltip text="Client's marginal federal income tax bracket. Common rates: 22% ($47k-$100k), 24% ($100k-$191k), 32% ($191k-$244k), 35% ($244k-$609k), 37% ($609k+).">
                        <label style={fieldLabelStyle}>Federal Tax Bracket</label>
                      </InfoTooltip>
                      <select value={taxRate} onChange={e => setTaxRate(Number(e.target.value))} style={selectStyle}>
                        {[0,10,12,22,24,32,35,37].map(r => <option key={r} value={r}>{r === 0 ? '0% (SE tax only)' : r + '%'}</option>)}
                      </select>
                    </div>
                    <div>
                      <InfoTooltip text="Select the client's state. Tax rate auto-fills based on each state's top marginal income tax rate. 0% in TX, FL, WY, NV, TN, WA, SD, AK, NH.">
                        <label style={fieldLabelStyle}>State</label>
                      </InfoTooltip>
                      <select value={stateCode} onChange={e => setStateCode(e.target.value)} style={selectStyle}>
                        {STATE_TAX_RATES.map(s => (
                          <option key={s.code} value={s.code}>{s.label} ({s.rate}%)</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Row 2: Income */}
                  <div className="text-xs mb-3" style={{ color: '#737373', fontStyle: 'italic' }}>
                    Mining income comes from this plan's configuration. Enter the client's other income below to model SE tax interactions.
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                    <div>
                      <InfoTooltip text="Net profit from the client's existing business (not mining). Schedule C or K-1 income from non-mining activities. Mining losses from depreciation can offset this income, reducing SE tax.">
                        <label style={fieldLabelStyle}>Other Business Income</label>
                      </InfoTooltip>
                      <div style={{ position: 'relative' }}>
                        <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#737373', fontSize: '14px', fontFamily: "'IBM Plex Mono', monospace" }}>$</span>
                        <input type="number" value={otherSEIncome} onChange={e => setOtherSEIncome(Number(e.target.value))} style={{ ...inputStyle, paddingLeft: '24px' }} min={0} max={5e6} step={5000} />
                      </div>
                      <div className="text-xs mt-1" style={{ color: '#737373' }}>Non-mining business (if any)</div>
                    </div>
                    <div>
                      <InfoTooltip text="W-2 salary from this LLC. Consumes Social Security wage base, reducing SS tax on mining and business income.">
                        <label style={fieldLabelStyle}>W-2 Salary</label>
                      </InfoTooltip>
                      <div style={{ position: 'relative' }}>
                        <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#737373', fontSize: '14px', fontFamily: "'IBM Plex Mono', monospace" }}>$</span>
                        <input type="number" value={w2SSWages} onChange={e => setW2SSWages(Number(e.target.value))} style={{ ...inputStyle, paddingLeft: '24px' }} min={0} max={1e6} step={5000} />
                      </div>
                      <div className="text-xs mt-1" style={{ color: '#737373' }}>
                        Salary from this LLC
                      </div>
                    </div>
                    <div>
                      <InfoTooltip text="Does the client actively participate in mining operations (500+ hrs/year)? Active = subject to SE tax but can use depreciation losses against other income. Passive = no SE tax but losses may be suspended.">
                        <label style={fieldLabelStyle}>Mining Participation</label>
                      </InfoTooltip>
                      <select value={incomeType} onChange={e => setIncomeType(e.target.value)} style={selectStyle}>
                        <option value="active">Active (Material Participation)</option>
                        <option value="distributions_only">Passive (Investor Only)</option>
                      </select>
                      <div className="text-xs mt-1" style={{ color: '#737373' }}>
                        {incomeType === 'active' ? 'Subject to SE tax, full deductions' : 'No SE tax, losses may suspend'}
                      </div>
                    </div>
                  </div>

                  {/* S-Corp salary % — only when S-Corp selected */}
                  {isScorp && (
                    <div className="p-3 mb-4" style={{ borderRadius: '6px', background: '#F5F3FF', border: '1px solid #DDD6FE' }}>
                      <div className="text-xs font-medium mb-1" style={{ color: '#7C3AED', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Mining Profit Split (S-Corp)</div>
                      <div className="text-xs mb-2" style={{ color: '#8B5CF6' }}>Based on {fmt(operatorEconomics?.miningNetProfit || 0)}/yr mining profit after depreciation</div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 items-end">
                        <div>
                          <InfoTooltip text="IRS requires 'reasonable compensation' for S-Corp owner-employees. Typically 25-40% of mining net profit goes to W-2 salary; the rest flows as K-1 distributions. Lower salary = lower payroll taxes, but too low increases audit risk.">
                            <label style={fieldLabelStyle}>Salary % of Mining Profit</label>
                          </InfoTooltip>
                          <input type="number" value={scorpSalaryPercent} onChange={e => setScorpSalaryPercent(Number(e.target.value))} style={inputStyle} min={10} max={60} step={5} />
                        </div>
                        <div className="text-sm" style={{ color: '#525252' }}>
                          <span style={{ fontWeight: 500 }}>= {fmt(operatorEconomics?.scorpSalary || 0)}/yr</span> salary
                          <br />
                          <span style={{ color: '#737373' }}>+ {fmt(operatorEconomics?.scorpDistribution || 0)}/yr</span> distributions
                        </div>
                        <div className="text-xs" style={{ color: '#7C3AED' }}>
                          Payroll tax applies to salary only. Distributions pass through without SE/payroll tax.
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Quick summary of combined rate */}
                  <div className="flex items-center gap-4 text-sm flex-wrap" style={{ color: '#525252' }}>
                    <span>Combined income tax rate: <strong style={{ color: '#1A1A1A' }}>{(effectiveIncomeTaxRate + stateTaxRate).toFixed(1).replace(/\.0$/, '')}%</strong>{feieCoversIncome && taxRate > effectiveIncomeTaxRate ? <span style={{ color: '#737373' }}> (FEIE brings {taxRate}% down to {effectiveIncomeTaxRate.toFixed(1).replace(/\.0$/, '')}%)</span> : ''}</span>
                    <span style={{ color: '#E5E5E5' }}>|</span>
                    <span>SS wage base remaining after other income: <strong style={{ color: '#1A1A1A' }}>{fmt(Math.max(0, SE_TAX.ssWageBase - w2SSWages - (otherSEIncome * SE_TAX.netEarningsFactor)))}</strong></span>
                  </div>

                  {/* FEIE Banner */}
                  {feieApplies && (
                    <div className="p-3 rounded-lg text-sm" style={{ background: '#EFF6FF', border: '1px solid #BFDBFE' }}>
                      <div className="font-semibold mb-1" style={{ color: '#1E40AF' }}>
                        FEIE Applied (IRC §911 Foreign Earned Income Exclusion)
                      </div>
                      <div style={{ color: '#1E3A5F' }}>
                        Exclusion: <strong>{fmt(FEIE_EXCLUSION)}</strong>
                        {' · '}Other earned income: <strong>{fmt((otherSEIncome || 0) + (w2SSWages || 0))}</strong>
                        {' · '}FEIE remaining: <strong>{fmt(feieRemaining)}</strong>
                        {feieCoversIncome
                          ? <span>: <span style={{ color: '#15803D' }}>FEIE covers all earned income. Federal income tax rate set to 0%.</span></span>
                          : <span>: Income exceeds FEIE by <strong>{fmt(Math.abs(feieRemaining))}</strong>. Set bracket for excess.</span>
                        }
                      </div>
                      <div className="mt-1" style={{ color: '#6B7280', fontSize: '11px' }}>
                        SE tax (15.3%) still applies in full. FEIE does not exempt self-employment tax.
                        {foreignTaxRegime === 'portugal_nhr' && ' Portugal NHR: foreign-source SE income exempt from Portuguese tax.'}
                      </div>
                    </div>
                  )}
                </div>

                {/* ═══════ SECTION 2: Advanced Settings (Collapsed) ═══════ */}
                <div style={{ ...S.innerCard, borderRadius: '8px', overflow: 'hidden' }}>
                  <button
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    className="w-full flex items-center justify-between p-3 text-left no-print"
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
                  >
                    <span className="text-sm font-medium" style={{ color: '#525252' }}>
                      Advanced Settings: Depreciation, International
                    </span>
                    {showAdvanced ? <ChevronUp className="w-4 h-4" style={{ color: '#737373' }} /> : <ChevronDown className="w-4 h-4" style={{ color: '#737373' }} />}
                  </button>

                  {/* Content: hidden on screen when collapsed, always visible in print */}
                  <div className={showAdvanced ? '' : 'print-only screen-hidden'}>
                    <div className="px-4 pb-4 space-y-4">
                      {/* Depreciation */}
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        <div>
                          <InfoTooltip text="Section 168(k), restored to 100% through 2029 by OBBBA (July 2025). Deduct the full cost of miners in the year of purchase. Always applied.">
                            <label style={fieldLabelStyle}>Bonus Depreciation</label>
                          </InfoTooltip>
                          <div className="px-3 py-2 text-sm" style={{ ...inputStyle, background: '#F5F5F5', color: S.C.green }}>
                            100% (§168(k) active)
                          </div>
                        </div>
                        <div>
                          <InfoTooltip text="Auto-detected from your state selection. CA, NY, NJ, PA, AR, HI, MD, MS do not conform to federal bonus depreciation, so state taxes allow MACRS only.">
                            <label style={fieldLabelStyle}>State Bonus Dep?</label>
                          </InfoTooltip>
                          <div className="px-3 py-2 text-sm" style={{ ...inputStyle, background: '#F5F5F5', color: stateConformsBonus ? S.C.green : S.C.orange }}>
                            {stateConformsBonus ? 'Yes, full bonus' : 'No, MACRS only'}
                          </div>
                        </div>
                        <div className="flex items-end">
                          <div className="text-xs pb-1" style={{ color: qbiEnabled ? S.C.green : '#737373' }}>
                            QBI (§199A): {qbiEnabled ? 'Auto-applied (pass-through)' : 'N/A (C-Corp)'}
                          </div>
                        </div>
                      </div>

                      {/* International */}
                      <div>
                        <div className="text-xs font-medium mb-2" style={{ color: '#737373', textTransform: 'uppercase', letterSpacing: '0.06em' }}>International</div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <div>
                            <label style={fieldLabelStyle}>Tax Residency</label>
                            <select value={ownerResidency} onChange={e => setOwnerResidency(e.target.value)} style={selectStyle}>
                              <option value="us_resident">US Resident</option>
                              <option value="us_citizen_abroad">US Citizen Abroad</option>
                              <option value="nra_treaty">NRA (Treaty)</option>
                              <option value="nra_no_treaty">NRA (No Treaty)</option>
                            </select>
                          </div>
                          <div>
                            <label style={fieldLabelStyle}>LLC Management</label>
                            <select value={llcManagementLocation} onChange={e => setLLCManagementLocation(e.target.value)} style={selectStyle}>
                              <option value="us">United States</option>
                              <option value="uk">United Kingdom</option>
                              <option value="other_foreign">Other Foreign</option>
                            </select>
                          </div>
                          <div>
                            <label style={fieldLabelStyle}>Foreign Regime</label>
                            <select value={foreignTaxRegime} onChange={e => setForeignTaxRegime(e.target.value)} style={selectStyle}>
                              <option value="none">None / Standard</option>
                              <option value="portugal_nhr">Portugal NHR / IFICI</option>
                              <option value="uk_resident">UK Tax Resident</option>
                              <option value="other_treaty">Other Treaty</option>
                            </select>
                          </div>
                        </div>
                      </div>

                    </div>
                  </div>
                </div>

                {/* ═══════ SECTION 3: Mining Tax Results ═══════ */}
                {taxStructureComparison && (
                  <>
                    {/* Summary Banner */}
                    <div className="p-4" style={{
                      borderRadius: '8px',
                      background: taxStructureComparison.miningVsDCASavings > 0 ? '#F0FDF4' : '#FFF7ED',
                      border: `1px solid ${taxStructureComparison.miningVsDCASavings > 0 ? '#BBF7D0' : '#FED7AA'}`,
                    }}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-bold text-lg" style={{ color: '#1A1A1A' }}>
                          Mining Tax Advantage:{' '}
                          <span style={{ color: taxStructureComparison.miningVsDCASavings > 0 ? S.C.green : S.C.orange }}>
                            {taxStructureComparison.miningVsDCASavings > 0
                              ? `${fmt(taxStructureComparison.miningVsDCASavings)} saved vs DCA`
                              : `DCA is ${fmt(Math.abs(taxStructureComparison.miningVsDCASavings))} cheaper`
                            }
                          </span>
                        </span>
                      </div>
                      <div className="text-sm" style={{ color: '#333333' }}>
                        {useBonusDepreciation
                          ? `Section 168(k) provides 100% bonus depreciation on each miner purchase, saving ${fmt(taxStructureComparison.depreciationTaxSavings)} in income taxes${(taxStructureComparison.depreciationSETaxSavings || 0) > 0 ? ` + ${fmt(taxStructureComparison.depreciationSETaxSavings)} in SE taxes` : ''} at your ${effectiveIncomeTaxRate + stateTaxRate}% combined rate. OpEx deductions save an additional ${fmt(taxStructureComparison.opExTaxSavings + (taxStructureComparison.opExSETaxSavings || 0))} over ${timeHorizon} years.`
                          : `MACRS 5-year depreciation on ${fmt(taxStructureComparison.totalCapEx)} total CapEx, saving ${fmt(taxStructureComparison.depreciationTaxSavings)} in taxes over the depreciation schedule.`
                        }
                      </div>
                      {taxStructureComparison.international?.isPortugalNHR && taxStructureComparison.international?.hasUKManagement && (
                        <div className="text-xs mt-2" style={{ color: S.C.purple }}>Portugal NHR: UK-managed LLC distributions may be exempt from Portuguese tax under DTA</div>
                      )}
                    </div>

                    {/* Key Tax Metrics */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {[
                        { label: 'Total CapEx', value: fmt(taxStructureComparison.totalCapEx), color: '#1A1A1A' },
                        { label: 'Depreciation Tax Savings', value: fmt(taxStructureComparison.depreciationTaxSavings + (taxStructureComparison.depreciationSETaxSavings || 0)), color: S.C.green, sub: useBonusDepreciation ? `168(k): income ${fmt(taxStructureComparison.depreciationTaxSavings)} + SE ${fmt(taxStructureComparison.depreciationSETaxSavings || 0)}` : 'MACRS 5-year schedule' },
                        { label: 'OpEx Tax Savings', value: fmt(taxStructureComparison.opExTaxSavings + (taxStructureComparison.opExSETaxSavings || 0)), color: S.C.green, sub: `Income: ${fmt(taxStructureComparison.opExTaxSavings)} + SE: ${fmt(taxStructureComparison.opExSETaxSavings || 0)} over ${timeHorizon}yr` },
                        {
                          label: isCcorp ? 'Total Entity Tax/yr' : 'Total Payroll/SE Tax/yr',
                          value: fmt(taxStructureComparison.annualTotalPayrollTax),
                          color: taxStructureComparison.annualTotalPayrollTax > 0 ? S.C.red : S.C.green,
                          sub: isCcorp ? `Corp tax: 21% (${fmt(taxStructureComparison.annualCCorpTax)})`
                            : isScorp ? `Mining payroll: ${fmt(taxStructureComparison.annualMiningSETax)} + Business SE: ${fmt(taxStructureComparison.annualOtherSETax)}`
                            : taxStructureComparison.isPassive ? `Mining: passive ($0) + Business SE: ${fmt(taxStructureComparison.annualOtherSETax)}`
                            : taxStructureComparison.annualMiningSETax < 0
                              ? `Mining loss reduces SE by ${fmt(Math.abs(taxStructureComparison.annualMiningSETax))}/yr · Business SE: ${fmt(taxStructureComparison.annualOtherSETax)}`
                              : `Mining SE: ${fmt(taxStructureComparison.annualMiningSETax)} + Business SE: ${fmt(taxStructureComparison.annualOtherSETax)}`
                        },
                      ].map((item, i) => (
                        <div key={i} className="p-3" style={{ ...S.innerCard, borderRadius: '8px' }}>
                          <div style={S.labelSm}>{item.label}</div>
                          <div className="text-lg font-bold" style={{ color: item.color }}>{item.value}</div>
                          {item.sub && <div className="text-xs" style={{ color: '#737373' }}>{item.sub}</div>}
                        </div>
                      ))}
                    </div>

                    {/* Mining Tax Benefits Waterfall */}
                    <div className="p-3" style={{ borderRadius: '8px', background: '#F0FDF4', border: '1px solid #BBF7D0' }}>
                      <h4 className="font-medium mb-1" style={{ color: S.C.green }}>Mining Tax Benefits ({timeHorizon}-Year Horizon)</h4>
                      <div className="text-xs mb-3" style={{ color: '#737373' }}>How mining deductions reduce your effective cost of acquiring BTC</div>
                      <div className="space-y-1.5 text-sm">
                        {[
                          ['Depreciation Income Tax Savings', `+${fmt(taxStructureComparison.depreciationTaxSavings)}`, S.C.green, useBonusDepreciation ? `168(k) 100% bonus dep @ ${effectiveIncomeTaxRate + stateTaxRate}% income tax rate` : `MACRS 5-year schedule on ${fmt(taxStructureComparison.totalCapEx)} total CapEx`],
                          ...((taxStructureComparison.depreciationSETaxSavings || 0) > 0 ? [['Depreciation SE Tax Savings', `+${fmt(taxStructureComparison.depreciationSETaxSavings)}`, S.C.green, `Depreciation loss offsets combined SE income, which reduces the 15.3% SE tax on all business income`]] : []),
                          ['OpEx Income Tax Deductions', `+${fmt(taxStructureComparison.opExTaxSavings)}`, S.C.green, `Electricity, hosting, pool fees deductible at ${effectiveIncomeTaxRate + stateTaxRate}% combined rate`],
                          ...((taxStructureComparison.opExSETaxSavings || 0) > 0 ? [['OpEx SE Tax Savings', `+${fmt(taxStructureComparison.opExSETaxSavings)}`, S.C.green, `OpEx reduces net SE earnings, which lowers the 15.3% SE tax on combined business income`]] : []),
                          ...(taxStructureComparison.qbiTaxSavings > 0 ? [['QBI Deduction (§199A)', `+${fmt(taxStructureComparison.qbiTaxSavings)}`, S.C.green, isScorp ? `20% of K-1 distributions (${fmt(operatorEconomics?.scorpDistribution || 0)})` : `20% of qualified business income`]] : []),
                          [isScorp ? 'Payroll Tax' : isCcorp ? 'Corp Tax' : 'Mining SE Tax',
                            isCcorp ? `-${fmt(taxStructureComparison.totalCCorpTax)}`
                              : taxStructureComparison.totalMiningSETax > 0 ? `-${fmt(taxStructureComparison.totalMiningSETax)}`
                              : taxStructureComparison.totalMiningSETax < 0 ? `+${fmt(Math.abs(taxStructureComparison.totalMiningSETax))}`
                              : '$0',
                            isCcorp ? S.C.red
                              : taxStructureComparison.totalMiningSETax > 0 ? S.C.red
                              : S.C.green,
                            isCcorp ? '21% flat corporate tax, no SE tax'
                              : isScorp ? `Payroll on ${fmt(operatorEconomics?.scorpSalary || 0)} salary saves ${fmt(taxStructureComparison.annualScorpSavings)}/yr vs LLC`
                              : taxStructureComparison.isPassive ? 'Passive income, no self-employment tax'
                              : taxStructureComparison.totalMiningSETax < 0 ? 'Mining loss offsets other SE income for a net SE reduction'
                              : `15.3% SE tax on ${fmt(operatorEconomics?.miningSEBase || 0)} (92.35% of profit after depreciation)`
                          ],
                          ...(taxStructureComparison.totalMiningNIIT > 0 ? [['NIIT (3.8%, passive investor)', `-${fmt(taxStructureComparison.totalMiningNIIT)}`, S.C.red, 'IRC §1411 Net Investment Income Tax. Passive income above the AGI threshold owes 3.8% even though SE tax does not apply']] : []),
                          ...(otherSEIncome > 0 ? [['Existing Business SE Tax', taxStructureComparison.totalOtherSETax > 0 ? `-${fmt(taxStructureComparison.totalOtherSETax)}` : '$0 (exempt)', taxStructureComparison.totalOtherSETax > 0 ? S.C.red : S.C.green, taxStructureComparison.seTaxExemptReason ? `Exempt: ${taxStructureComparison.seTaxExemptReason}` : `SE tax on ${fmt(otherSEIncome)}/yr business income (15.3% rate)`]] : []),
                        ].map(([label, value, color, hint], i) => (
                          <div key={i}>
                            <div className="flex justify-between items-baseline">
                              {hint
                                ? <InfoTooltip text={hint}><span style={{ color: '#525252' }}>{label}</span></InfoTooltip>
                                : <span style={{ color: '#525252' }}>{label}</span>}
                              <span style={{ color, fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
                            </div>
                            {hint && <div className="print-only text-xs" style={{ color: '#737373' }}>{hint}</div>}
                          </div>
                        ))}
                        <div className="flex justify-between font-bold pt-2 mt-2" style={{ borderTop: '1px solid rgba(0,0,0,0.08)' }}>
                          <span style={{ color: '#1A1A1A' }}>Total Mining Tax Benefit</span>
                          <span style={{ color: S.C.green }}>+{fmt(taxStructureComparison.totalMiningTaxBenefit)}</span>
                        </div>

                        {/* Mining vs DCA quick comparison */}
                        <div className="pt-3 mt-2" style={{ borderTop: '1px solid rgba(0,0,0,0.08)' }}>
                          <div className="flex justify-between text-xs" style={{ color: '#525252' }}>
                            <span>Gross Mining Cost ({taxStructureComparison.totalBTCMined?.toFixed(2)} BTC)</span>
                            <span>{fmt(taxStructureComparison.grossMiningCost)}</span>
                          </div>
                          <div className="flex justify-between text-xs" style={{ color: S.C.green }}>
                            <span>Tax Deductions</span>
                            <span>-{fmt(taxStructureComparison.totalMiningTaxBenefit)}</span>
                          </div>
                          <div className="flex justify-between text-xs font-medium mt-1" style={{ color: '#1A1A1A' }}>
                            <span>Net Mining Cost</span>
                            <span>{fmt(taxStructureComparison.netMiningCost)}</span>
                          </div>
                          <div className="flex justify-between text-xs" style={{ color: S.C.blue }}>
                            <span>DCA Cost (same BTC at spot)</span>
                            <span>{fmt(taxStructureComparison.dcaCost)}</span>
                          </div>
                          <div className="flex justify-between text-xs font-bold mt-1" style={{ color: taxStructureComparison.miningVsDCASavings > 0 ? S.C.green : S.C.orange }}>
                            <span>{taxStructureComparison.miningVsDCASavings > 0 ? 'You save by mining' : 'DCA is cheaper by'}</span>
                            <span>{fmt(Math.abs(taxStructureComparison.miningVsDCASavings))}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Annual Operating Expenses */}
                    {operatorEconomics && (
                      <div className="p-3" style={{ ...S.innerCard, borderRadius: '8px' }}>
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium" style={{ color: S.C.cyan }}>Annual Operating Expenses</h4>
                          <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: '#F0F9FF', color: S.C.blue }}>
                            Deductible against income tax and SE tax
                          </span>
                        </div>
                        <div className="grid grid-cols-5 gap-2 text-sm">
                          {[
                            ['Electricity', operatorEconomics.afterTaxOpEx?.electricity, null, 'Pre-tax'],
                            ['Hosting', operatorEconomics.afterTaxOpEx?.hosting, null, 'Pre-tax'],
                            ['Pool Fees', operatorEconomics.afterTaxOpEx?.poolFees, null, 'Pre-tax'],
                            ['Tax Savings', operatorEconomics.opexTaxSavings, S.C.green, `Income + SE tax`],
                            ['Net Cost', operatorEconomics.netOpExAfterTax, S.C.blue, 'After-tax cost'],
                          ].map(([l, v, c, sub], i) => (
                            <div key={i}>
                              <span style={S.labelSm}>{l}</span>
                              <div className="font-medium" style={{ color: c || '#1A1A1A' }}>{i === 3 ? `-${fmt(v || 0)}` : fmt(v || 0)}</div>
                              <div className="text-xs" style={{ color: '#737373' }}>{sub}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Entity Comparison Table */}
                    {taxStructureComparison.entityComparison && !taxStructureComparison.isPassive && (
                      <div className="p-3" style={{ ...S.innerCard, borderRadius: '8px' }}>
                        <h4 className="font-medium mb-3" style={{ color: S.C.purple }}>Entity Tax Comparison (Annual Mining Profit: {fmt(operatorEconomics?.miningNetProfit || 0)})</h4>
                        <div className="grid grid-cols-3 gap-3">
                          {[
                            {
                              label: 'LLC / Sole Prop',
                              active: entityType === 'llc' || entityType === 'mmllc_foreign',
                              items: [
                                ['Income Tax', fmt(taxStructureComparison.entityComparison.llc.incomeTax)],
                                ['SE Tax (15.3%)', fmt(taxStructureComparison.entityComparison.llc.seTax), S.C.red],
                                ...(qbiEnabled ? [['QBI Savings', `-${fmt(taxStructureComparison.entityComparison.llc.qbiSavings)}`, S.C.green]] : []),
                              ],
                              total: taxStructureComparison.entityComparison.llc.total,
                              color: S.C.blue,
                            },
                            {
                              label: 'S-Corp',
                              active: isScorp,
                              items: [
                                ['W-2 Salary', fmt(taxStructureComparison.entityComparison.scorp.salary), '#525252'],
                                ['K-1 Distribution', fmt(taxStructureComparison.entityComparison.scorp.distribution), '#525252'],
                                ['Income Tax', fmt(taxStructureComparison.entityComparison.scorp.incomeTax)],
                                ['Payroll Tax', fmt(taxStructureComparison.entityComparison.scorp.payrollTax), S.C.red],
                                ...(qbiEnabled ? [['QBI Savings', `-${fmt(taxStructureComparison.entityComparison.scorp.qbiSavings)}`, S.C.green]] : []),
                              ],
                              total: taxStructureComparison.entityComparison.scorp.total,
                              savings: taxStructureComparison.entityComparison.scorp.savingsVsLLC,
                              color: S.C.green,
                            },
                            {
                              label: 'C-Corp',
                              active: isCcorp,
                              items: [
                                ['Corp Tax (21%)', fmt(taxStructureComparison.entityComparison.ccorp.corpTax), S.C.red],
                                ['SE Tax', '$0', S.C.green],
                                ['QBI', 'N/A (C-Corp)', '#737373'],
                              ],
                              total: taxStructureComparison.entityComparison.ccorp.total,
                              note: 'Dividends taxed separately',
                              color: S.C.orange,
                            },
                          ].map((entity, i) => (
                            <div key={i} className="p-3" style={{
                              borderRadius: '8px',
                              background: entity.active ? '#F5F3FF' : '#FCFCFC',
                              border: entity.active ? '2px solid #DDD6FE' : '1px solid #E5E5E5',
                            }}>
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-medium text-sm" style={{ color: '#1A1A1A' }}>{entity.label}</span>
                                {entity.active && <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: '#7C3AED', color: '#FCFCFC' }}>Active</span>}
                              </div>
                              <div className="space-y-1">
                                {entity.items.map(([l, v, c], j) => (
                                  <div key={j} className="flex justify-between text-xs">
                                    <span style={{ color: '#525252' }}>{l}</span>
                                    <span style={{ color: c || '#1A1A1A' }}>{v}</span>
                                  </div>
                                ))}
                              </div>
                              <div className="flex justify-between font-bold text-sm mt-2 pt-2" style={{ borderTop: '1px solid #E5E5E5' }}>
                                <span style={{ color: '#1A1A1A' }}>Total Tax</span>
                                <span style={{ color: entity.color }}>{fmt(entity.total)}</span>
                              </div>
                              {entity.savings > 0 && (
                                <div className="text-xs mt-1" style={{ color: S.C.green }}>Saves {fmt(entity.savings)}/yr vs LLC</div>
                              )}
                              {entity.note && (
                                <div className="text-xs mt-1" style={{ color: '#737373' }}>{entity.note}</div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* EBL Warning */}
                    {taxStructureComparison.eblApplies && (
                      <div className="p-3" style={{ borderRadius: '8px', background: '#FEF2F2', border: '1px solid #FECACA' }}>
                        <div className="flex items-center gap-2 mb-1">
                          <AlertTriangle className="w-4 h-4" style={{ color: S.C.red }} />
                          <h4 className="font-medium text-sm" style={{ color: S.C.red }}>Excess Business Loss (EBL) Limitation, IRC §461(l)</h4>
                        </div>
                        <div className="text-xs" style={{ color: '#333333' }}>
                          Your business loss of {fmt(taxStructureComparison.eblExcess + taxStructureComparison.eblLimit)} exceeds the {filingStatus === 'mfj' ? 'MFJ' : 'single'} limit of {fmt(taxStructureComparison.eblLimit)}.
                          The excess {fmt(taxStructureComparison.eblExcess)} carries forward as a Net Operating Loss (NOL). It&apos;s not lost, but you cannot deduct it in the current year.
                        </div>
                      </div>
                    )}

                    {/* Passive Activity Warning */}
                    {taxStructureComparison.passiveActivityWarning && (
                      <div className="p-3" style={{ borderRadius: '8px', background: '#FFFBEB', border: '1px solid #FDE68A' }}>
                        <div className="flex items-center gap-2 mb-1">
                          <AlertTriangle className="w-4 h-4" style={{ color: '#D97706' }} />
                          <h4 className="font-medium text-sm" style={{ color: '#D97706' }}>Passive Activity Loss Warning, IRC §469</h4>
                        </div>
                        <div className="text-xs" style={{ color: '#333333' }}>
                          Mining participation is set to &quot;Passive.&quot; Depreciation losses of {fmt(taxStructureComparison.depreciationTaxSavings)} may be <strong>suspended</strong> under
                          passive activity rules if you have no other passive income to offset. To take depreciation deductions against active/W-2 income, you must materially
                          participate (500+ hours/year). Consider setting participation to &quot;Active&quot; if the client meets material participation requirements.
                        </div>
                      </div>
                    )}

                    <div className="text-xs p-3" style={{ borderRadius: '8px', background: '#FFFBEB', border: '1px solid #FDE68A', color: '#333333' }}>
                      Section 168(k) bonus depreciation restored to 100% by OBBBA (July 2025). Mining equipment qualifies as 5-year MACRS property.
                      Section 179 limit: $2.56M (2026). QBI deduction (§199A) provides 20% off for pass-through entities. Consult a qualified CPA/tax attorney for your specific situation.
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* DCA vs Mining Tax-Aware Comparison */}
      {dcaVsMiningComparison && showAfterTaxEconomics && (
        <div className="overflow-hidden print-deliverable" style={{ ...S.card, borderRadius: '8px' }}>
          <div className="p-5">
            <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ color: '#525252', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              <TrendingUp className="w-4 h-4" style={{ color: S.C.blue }} /> DCA vs Mining: Tax-Adjusted Comparison
            </h3>

            {/* Summary Banner */}
            <div className="p-4 mb-4" style={{
              borderRadius: '8px',
              background: dcaVsMiningComparison.userAnalysis.miningIsCheaper ? '#F0FDF4' : '#FFF7ED',
              border: `1px solid ${dcaVsMiningComparison.userAnalysis.miningIsCheaper ? '#BBF7D0' : '#FED7AA'}`,
            }}>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{
                  background: dcaVsMiningComparison.userAnalysis.miningIsCheaper ? '#DCFCE7' : '#FFEDD5',
                }}>
                  <Cpu className="w-5 h-5" style={{ color: dcaVsMiningComparison.userAnalysis.miningIsCheaper ? S.C.green : S.C.orange }} />
                </div>
                <div>
                  <div className="font-bold text-lg" style={{ color: '#1A1A1A' }}>
                    At your {dcaVsMiningComparison.userTaxRate}% combined rate:{' '}
                    <span style={{ color: dcaVsMiningComparison.userAnalysis.miningIsCheaper ? S.C.green : S.C.orange }}>
                      {dcaVsMiningComparison.userAnalysis.miningIsCheaper ? 'Mining is cheaper' : 'DCA is cheaper'}
                    </span>
                  </div>
                  <div className="text-sm" style={{ color: '#525252' }}>
                    Mining net cost: {fmt(dcaVsMiningComparison.userAnalysis.netCostPerBTC)}/BTC vs DCA at {fmt(dcaVsMiningComparison.avgSpotPrice)}/BTC
                    {' '}({dcaVsMiningComparison.userAnalysis.miningIsCheaper
                      ? `saving ${fmt(Math.abs(dcaVsMiningComparison.userAnalysis.miningAdvantage))} total`
                      : `costing ${fmt(Math.abs(dcaVsMiningComparison.userAnalysis.miningAdvantage))} more`
                    })
                  </div>
                </div>
              </div>
            </div>

            {/* Cost Comparison Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
              <div className="p-4" style={{ borderRadius: '8px', background: '#FFF7ED', border: '1px solid #FED7AA' }}>
                <div style={S.labelSm}>Mining Gross Cost</div>
                <div className="text-xl font-bold mt-1" style={S.tn(S.C.orange)}>
                  {fmt(dcaVsMiningComparison.grossCostPerBTC)}/BTC
                </div>
                <div className="text-xs mt-1" style={{ color: '#525252' }}>Before tax deductions</div>
              </div>
              <div className="p-4" style={{ borderRadius: '8px', background: '#F0FDF4', border: '1px solid #BBF7D0' }}>
                <div style={S.labelSm}>Mining Net Cost</div>
                <div className="text-xl font-bold mt-1" style={S.tn(S.C.green)}>
                  {fmt(dcaVsMiningComparison.userAnalysis.netCostPerBTC)}/BTC
                </div>
                <div className="text-xs mt-1" style={{ color: '#525252' }}>After {dcaVsMiningComparison.userTaxRate}% tax deductions</div>
              </div>
              <div className="p-4" style={{ borderRadius: '8px', background: '#EFF6FF', border: '1px solid #BFDBFE' }}>
                <div style={S.labelSm}>DCA Spot Price</div>
                <div className="text-xl font-bold mt-1" style={S.tn(S.C.blue)}>
                  {fmt(dcaVsMiningComparison.avgSpotPrice)}/BTC
                </div>
                <div className="text-xs mt-1" style={{ color: '#525252' }}>Avg projected spot price</div>
              </div>
            </div>

            {/* Tax Rate Curve Chart */}
            <div className="p-4 mb-4" style={{ ...S.innerCard, borderRadius: '8px' }}>
              <h4 className="text-sm font-medium mb-1" style={{ color: '#525252' }}>
                How Tax Deductions Lower Your Mining Cost
              </h4>
              <div className="text-xs mb-2" style={{ color: '#737373' }}>
                Higher tax brackets = bigger deductions = lower net cost to mine BTC. The green line shows your after-tax mining cost dropping as the tax rate increases.
              </div>
              <ResponsiveContainer width="100%" height={320}>
                <ComposedChart data={dcaVsMiningComparison.taxRateAnalysis} margin={{ top: 10, right: 30, left: 20, bottom: 10 }}>
                  <defs>
                    <linearGradient id="miningCostGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={S.C.green} stopOpacity={0.15} />
                      <stop offset="95%" stopColor={S.C.green} stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 6" stroke="#E5E5E5" />
                  <XAxis dataKey="taxRate" tickFormatter={v => `${v}%`} style={S.axis} tick={{ fill: '#737373', fontSize: 11 }} label={{ value: 'Combined Tax Rate', position: 'insideBottom', offset: -5, fill: '#737373', fontSize: 11 }} />
                  <YAxis tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} style={S.axis} tick={{ fill: '#737373', fontSize: 11 }} label={{ value: 'Total Cost to Acquire BTC', position: 'insideLeft', angle: -90, offset: -10, fill: '#737373', fontSize: 10 }} />
                  <Tooltip
                    contentStyle={S.ttip}
                    formatter={(value, name) => [fmt(value), name]}
                    labelFormatter={l => `Tax Rate: ${l}%`}
                  />
                  <Area type="monotone" dataKey="netMiningCost" stroke={S.C.green} fill="url(#miningCostGrad)" strokeWidth={2.5} name="Mining Cost (after deductions)" dot={false} />
                  <Line type="monotone" dataKey="dcaCost" stroke={S.C.blue} strokeWidth={2} strokeDasharray="8 4" name="DCA Cost (no deductions)" dot={false} />
                  {dcaVsMiningComparison.breakevenTaxRate !== null && (
                    <ReferenceLine x={Math.round(dcaVsMiningComparison.breakevenTaxRate)} stroke={S.C.orange} strokeWidth={2} strokeDasharray="4 4" label={{ value: `Breakeven: ${dcaVsMiningComparison.breakevenTaxRate.toFixed(1)}%`, position: 'top', fill: S.C.orange, fontSize: 12, fontWeight: 600 }} />
                  )}
                  <ReferenceLine x={dcaVsMiningComparison.userTaxRate} stroke={S.C.purple} strokeWidth={2} label={{ value: `Your Rate: ${dcaVsMiningComparison.userTaxRate}%`, position: 'insideTopRight', fill: S.C.purple, fontSize: 11, fontWeight: 500 }} />
                </ComposedChart>
              </ResponsiveContainer>
              <div className="flex items-center justify-center gap-6 mt-2 text-xs" style={{ color: '#525252' }}>
                <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 rounded" style={{ background: S.C.green, display: 'inline-block' }}></span> Mining Cost (after deductions)</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 rounded" style={{ background: S.C.blue, display: 'inline-block', borderTop: '2px dashed' + S.C.blue }}></span> DCA Cost (no deductions)</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 rounded" style={{ background: S.C.orange, display: 'inline-block' }}></span> Breakeven</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 rounded" style={{ background: S.C.purple, display: 'inline-block' }}></span> Your Rate</span>
              </div>
            </div>

            {/* Breakeven Insight Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
              <div className="p-4" style={{ ...S.innerCard, borderRadius: '8px' }}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: dcaVsMiningComparison.miningNeverCheaper ? '#FEF2F2' : '#FFF7ED' }}>
                    <TrendingUp className="w-4 h-4" style={{ color: dcaVsMiningComparison.miningNeverCheaper ? S.C.red : S.C.orange }} />
                  </div>
                  <div>
                    <div style={S.labelSm}>Breakeven (with 168(k) + Sec 179)</div>
                    <div className="text-lg font-bold" style={{ color: dcaVsMiningComparison.miningNeverCheaper ? S.C.red : dcaVsMiningComparison.miningAlwaysCheaper ? S.C.green : S.C.orange }}>
                      {dcaVsMiningComparison.breakevenTaxRate !== null
                        ? `${dcaVsMiningComparison.breakevenTaxRate.toFixed(1)}% combined rate`
                        : dcaVsMiningComparison.miningAlwaysCheaper
                          ? 'Mining always cheaper'
                          : 'DCA always cheaper'
                      }
                    </div>
                  </div>
                </div>
                <div className="text-xs" style={{ color: '#525252' }}>
                  {dcaVsMiningComparison.miningNeverCheaper
                    ? 'Even with tax incentives, mining costs exceed spot price at all tax rates'
                    : dcaVsMiningComparison.bonusDepRate > 0
                      ? `Includes ${(dcaVsMiningComparison.bonusDepRate * 100).toFixed(0)}% bonus depreciation for ${dcaVsMiningComparison.startYear}`
                      : 'Bonus depreciation has phased out'
                  }
                </div>
              </div>
              <div className="p-4" style={{ ...S.innerCard, borderRadius: '8px' }}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#FEF2F2' }}>
                    <AlertTriangle className="w-4 h-4" style={{ color: S.C.red }} />
                  </div>
                  <div>
                    <div style={S.labelSm}>Breakeven (MACRS only, no incentives)</div>
                    <div className="text-lg font-bold" style={{ color: dcaVsMiningComparison.miningNeverCheaperNoIncentives ? S.C.red : dcaVsMiningComparison.miningAlwaysCheaperNoIncentives ? S.C.green : S.C.red }}>
                      {dcaVsMiningComparison.breakevenNoIncentives !== null
                        ? `${dcaVsMiningComparison.breakevenNoIncentives.toFixed(1)}% combined rate`
                        : dcaVsMiningComparison.miningAlwaysCheaperNoIncentives
                          ? 'Mining always cheaper'
                          : 'DCA always cheaper'
                      }
                    </div>
                  </div>
                </div>
                <div className="text-xs" style={{ color: '#525252' }}>
                  {dcaVsMiningComparison.miningNeverCheaperNoIncentives
                    ? 'Without incentives, mining costs exceed spot price at all tax rates'
                    : 'Without Section 179 or 168(k) bonus depreciation. Only standard 5-year MACRS schedule.'
                  }
                </div>
              </div>
            </div>

            {/* Key Insight */}
            <div className="p-4" style={{ borderRadius: '8px', background: '#F5F3FF', border: '1px solid #DDD6FE' }}>
              <div className="flex items-start gap-3">
                <ArrowRight className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: S.C.purple }} />
                <div>
                  <div className="font-medium text-sm mb-1" style={{ color: '#1A1A1A' }}>Key Insight</div>
                  <div className="text-sm" style={{ color: '#333333' }}>
                    {dcaVsMiningComparison.miningAlwaysCheaper
                      ? 'Mining is cheaper than DCA across all tax rates. The operational costs are low enough that even without any tax deductions, mining produces BTC at below market price.'
                      : dcaVsMiningComparison.miningNeverCheaper
                        ? `DCA is cheaper than mining across all tax rates (up to 50%). Operational costs (hosting, electricity, etc.) are high enough that even maximum tax deductions can't bring mining below spot price. Consider reducing hosting costs or choosing more efficient hardware.`
                        : dcaVsMiningComparison.userAnalysis.miningIsCheaper
                          ? `At your ${dcaVsMiningComparison.userTaxRate}% combined rate, mining beats DCA by ${fmt(Math.abs(dcaVsMiningComparison.userAnalysis.miningAdvantage))} total. Your tax rate is above the ${dcaVsMiningComparison.breakevenTaxRate.toFixed(1)}% breakeven, so tax deductions (depreciation, OpEx write-offs) make mining significantly cheaper than buying BTC on the open market.`
                          : `At your ${dcaVsMiningComparison.userTaxRate}% combined rate, DCA is ${fmt(Math.abs(dcaVsMiningComparison.userAnalysis.miningAdvantage))} cheaper than mining. You'd need a combined rate above ${dcaVsMiningComparison.breakevenTaxRate.toFixed(1)}% for mining's tax deductions to overcome the operational costs. ${dcaVsMiningComparison.breakevenNoIncentives !== null ? `Without bonus depreciation/Section 179, you'd need ${dcaVsMiningComparison.breakevenNoIncentives.toFixed(1)}%+.` : ''}`
                    }
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
